import { useCallback, useRef } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { ApolloError, BaseMutationOptions } from '@apollo/client'
import Hotjar from '@hotjar/browser'
import {
  SubItems,
  SubItem,
} from '@engyalo/delivery-ui-components/lib/interfaces/catalog'
import { useLocation, useSearchParams } from 'react-router-dom'
import { IProductCart } from '../../graphQL/mutations/cartRemoveProduct/types'
import useCartRemoveProduct from '../../graphQL/mutations/cartRemoveProduct'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import onRemoveProduct from '../../utils/onRemoveProduct'
import { useGetCart } from './useGetCart'
import { debounceById } from '../../utils/debounce'
import { IGenericFunction, IProduct } from '../../interfaces'
import { onCartResponse } from '../../utils/onCartResponse'
import { Referrers } from '../../consts/defaultConfigValues/defaultConstants'
import { generateDataDogContext } from '../../utils/dataDog'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import { addAlert } from '../../redux/slices/config/config'
import { sendGoogleEvent } from '../../utils/sendGoogleAnalytics'
import validateApolloErrorCartAction from '../../utils/validateApolloErrorCartAction'
import {
  hasCartMutationTimedOut,
  reportCartMutationTimeout,
  signalOfMutation,
} from '../../utils/cartMutationTimeout'
import { selectDisableWhileSyncing } from '../../redux/skuSyncing'
import YaloAnalytics from '../../modules/analytics'
import formatDataForAnalytics from '../../utils/formatDataForAnalytics'
import { ITrackCartEventPayload } from '../../modules/interfaces'
import { calculateRemoveFinalValue } from '../../utils/cart/calculateRemoveFinalValue'
import { fireGTMEvent } from '../../utils/fireGTMEvent'
import {
  getAnalyticsItem,
  getGTMEventsPayload,
} from '../../utils/getAnalyticsItems'

const callbackDebounceTime = process.env.REACT_APP_CART_ACTION_DEBOUNCE_TIME
  ? +process.env.REACT_APP_CART_ACTION_DEBOUNCE_TIME
  : 500

export const useDeleteItemFromCart = () => {
  const getCartAction = useGetCart()
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const abortControllers = useRef<Record<string, AbortController>>({})
  const state = useAppSelector((stateRedux) => stateRedux)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const freegoodsAsSubitem = useAppSelector(
    (state) => !!state.defaultSlice.config?.options?.freegoodsAsSubitem,
  )

  const autoAdd = useAppSelector(
    (state) =>
      !!state.defaultSlice.sessionData?.configuration?.promotions?.autoAdd,
  )

  // Off by default. See the flag's docblock in interfaces/config.ts for the
  // library behaviour this requires before a storefront turns it on.
  const disableWhileSyncing = useAppSelector(selectDisableWhileSyncing)

  const {
    defaultSlice: { sessionId = '', storeName = '', sessionData },
    cartSlice: cart,
  } = state
  const { config } = state.defaultSlice
  const category = 'cart'
  let action = 'Remove from cart'

  /*
   * Reads the store when the response lands, not the render-time `state`.
   * The debounce timer is not cleared on unmount, so a decrement can be sent
   * after the shopper has left the page it was made on, and Apollo still calls
   * this when it answers. The snapshot is that page's last render, so rewriting
   * from it leaves the next page's lists at the old quantity. The same hazard,
   * and its tests, are in `useDeleteProductFromCart` (`useDeleteItemFromCart.ts`)
   * and `tests/deleteProductFromCartStaleSnapshot.test.tsx`.
   */
  const onCompleted = (data: IProductCart) => {
    if (data.cartRemoveProduct) {
      onCartResponse({
        data: data.cartRemoveProduct,
        state: store.getState(),
        dispatch,
      })
    }
  }

  const onError = (error: ApolloError, clientOptions?: BaseMutationOptions) => {
    const signal = signalOfMutation(clientOptions)
    const alertType = validateApolloErrorCartAction(
      error,
      AlertMessageType.RemoveItem,
      signal,
    )
    if (alertType) {
      const message = getAlertErrorMessage(alertType, config?.texts)
      dispatch(addAlert({ message }))
      getCartAction()
      if (hasCartMutationTimedOut(signal)) {
        reportCartMutationTimeout({
          error,
          sku: clientOptions?.variables?.cartRemoveProductData?.sku,
          operation: 'remove',
          caller: 'useDeleteItemFromCart > onError',
        })
        return
      }
      const context = generateDataDogContext({
        title: 'Remove product mutation error',
        extraInfo: { function: 'useDeleteItemFromCart > onError' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(error, context)
    }
  }

  const [removeProduct] = useCartRemoveProduct({ onCompleted, onError })

  const execRemoveCartMutation = (
    quantity: number,
    sku: string,
    product: IProduct,
    currency: string,
    referrer?: Referrers,
    subItems?: SubItems,
    replace?: boolean,
    packageName?: string,
  ) => {
    const controller = new window.AbortController()
    abortControllers.current[sku] = controller
    const analyticsItem = getAnalyticsItem(product)
    if (referrer) {
      action = `Remove from cart from ${referrer}`
    }
    Hotjar.event(action)
    sendGoogleEvent(category, action, sku, quantity)
    window.fbq('trackCustom', 'RemoveFromCart', { sku, quantity })
    const analyticsData: ITrackCartEventPayload = formatDataForAnalytics(
      referrer ?? location.pathname,
      quantity,
      cart,
      product,
      currency,
      sessionData?.customer?.phoneNumber || [''],
      sessionId,
      'removeFromCart',
      storeName,
      sessionData?.extraIds,
    )
    YaloAnalytics.trackCartEvent(analyticsData)
    const {
      currency: currencyCode,
      value,
      items,
    } = getGTMEventsPayload({
      product,
      quantity,
      config,
    })
    fireGTMEvent('commerce-remove_from_cart', {
      currency: currencyCode,
      value,
      items,
    })
    onRemoveProduct({
      removeProductFunction: removeProduct,
      sku,
      quantity,
      sessionId,
      storeName,
      referrer,
      controller,
      dispatch,
      subItems,
      replace,
      packageName,
      disableWhileSyncing,
    })
  }

  const callBackRemoveProductToCart = useCallback(
    debounceById(
      execRemoveCartMutation as IGenericFunction,
      callbackDebounceTime,
    ),
    [],
  )

  const removeItemAction = ({
    product,
    currency,
    referrer: referrerFromArgs,
    ...restArgs
  }: {
    product: IProduct
    value: number
    newValue: number
    currency: string
    referrer?: Referrers
    isBundle?: boolean
    newSubItems?: SubItem
    currentSubItems?: SubItems
    isPackage?: boolean
    isFromCounterButtons?: boolean
  }) => {
    try {
      const referrer = searchParams.get('referrer') || referrerFromArgs
      const {
        package: productPackage,
        sku,
        name,
        minQtyAllowed,
        promotionalQty,
      } = product
      const packageNameFromCart = productPackage?.name || null
      const newValueResult = calculateRemoveFinalValue({
        packageNameFromCart,
        name,
        minQtyAllowed,
        promotionalQty,
        freegoodsAsSubitem,
        autoAdd,
        ...restArgs,
      })
      if (newValueResult) {
        const { handledValue, cleanedSubItems, replaceCart, packageName } =
          newValueResult
        abortControllers.current[sku]?.abort()
        callBackRemoveProductToCart(
          sku,
          handledValue,
          sku,
          product,
          currency,
          referrer,
          cleanedSubItems,
          replaceCart,
          packageName || undefined,
        )
      }
    } catch (exception) {
      const context = generateDataDogContext({
        title: 'could not remove product from cart',
        extraInfo: {
          product,
          value: restArgs.value,
          newValue: restArgs.newValue,
          function: 'removeItemAction',
        },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(exception, context)
    }
  }
  return { removeItemAction, execRemoveCartMutation }
}

export default useDeleteItemFromCart
